import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { message } from 'antd';
import { CloseSquare } from 'iconsax-react';
import SectionTitle from '../../../components/atoms/SectionTitle';
import SectionDesc from '../../../components/atoms/SectionDesc';
import Button from '../../../components/atoms/Button';
import CustomModal from '../../../components/atoms/CustomModal';
import TitleText from '../../../components/atoms/TitleText';
import DefaultBannersTable from './components/DefaultBannersTable';
import SetYadBannerModal from './components/SetYadBannerModal';
import { useDeletePromotionDefaultContentMutation } from '../../../store/services/promotionsService';
import { PromotionDefaultContent } from '../../../types/promotionDefaultContents';

export default function YadDefaultPromotionsPage() {
  const { t } = useTranslation();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingBanner, setEditingBanner] = useState<any>(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedBannerToDelete, setSelectedBannerToDelete] = useState<PromotionDefaultContent | null>(null);

  const [deleteBanner, { isLoading: isDeleting }] = useDeletePromotionDefaultContentMutation();

  const handleAddClick = () => {
    setEditingBanner(null);
    setIsModalOpen(true);
  };

  const handleEditClick = (banner: any) => {
    setEditingBanner(banner);
    setIsModalOpen(true);
  };

  const handleOpenDelete = (banner: PromotionDefaultContent) => {
    setSelectedBannerToDelete(banner);
    setIsDeleteModalOpen(true);
  };

  const handleDelete = async () => {
    if (!selectedBannerToDelete) return;
    try {
      await deleteBanner(selectedBannerToDelete.id).unwrap();
      message.success(t('common.success', 'Deleted successfully'));
      setIsDeleteModalOpen(false);
      setSelectedBannerToDelete(null);
    } catch (error: any) {
      const errData = error?.data;
      const errorDetail = errData?.errors
        ? (Object.values(errData.errors)[0] as string)
        : null;
      message.error(
        errorDetail ||
          errData?.message ||
          t('common.error', 'Something went wrong')
      );
    }
  };

  return (
    <div className="flex flex-col gap-6 w-full max-w-7xl">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div className="flex flex-col gap-1">
          <SectionTitle
            text={t(
              'home.yad-default-promotions',
              'Yad Promotions Default banners'
            )}
          />
          <SectionDesc
            text={t(
              'home.yad-default-promotions-desc',
              'Add, edit, or remove subscription plans.'
            )}
          />
        </div>
        <Button
          variant="dark"
          className="rounded-xl w-56"
          onClick={handleAddClick}
        >
          {t('home.set-yad-banner', 'Set yad banner')}
        </Button>
      </div>

      {/* Home Page Section */}
      <div className="bg-white rounded-xl p-6 flex flex-col gap-6 shadow-sm border border-gray-100">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="flex flex-col gap-1">
            <h3 className="text-[#111928] text-2xl font-bold leading-none">
              {t('home.yad-default-banners-home', 'Yad default banners (Home page)')}
            </h3>
            <SectionDesc
              text={t('home.yad-default-banners-home-desc', 'Display all service coins applied in system')}
            />
          </div>
        </div>
        <DefaultBannersTable
          marketplaceScopeCode="MAIN_MARKETPLACE"
          onEdit={handleEditClick}
          onDelete={handleOpenDelete}
        />
      </div>

      {/* Category Page Section */}
      <div className="bg-white rounded-xl p-6 flex flex-col gap-6 shadow-sm border border-gray-100">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="flex flex-col gap-1">
            <h3 className="text-[#111928] text-2xl font-bold leading-none">
              {t('home.yad-default-banners-category', 'Yad default banners (Category page)')}
            </h3>
            <SectionDesc
              text={t('home.yad-default-banners-category-desc', 'Display all service coins applied in system')}
            />
          </div>
        </div>
        <DefaultBannersTable
          marketplaceScopeCode="CATEGORY_MARKETPLACE"
          onEdit={handleEditClick}
          onDelete={handleOpenDelete}
        />
      </div>

      <SetYadBannerModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        banner={editingBanner}
      />

      {/* Delete Confirmation Modal */}
      <CustomModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        title={null}
        centered
        width={715}
        closable={false}
      >
        <div className="flex flex-col justify-center text-center gap-2 py-4">
          <div className="w-12 h-12 rounded-[14px] bg-[#FEF2F2] flex items-center justify-center self-center mb-2">
            <CloseSquare
              size={24}
              className="text-[#EF4444]"
              variant="Outline"
            />
          </div>

          <TitleText
            text={t('home.delete-item-title', 'Delete item')}
            size="2xl"
            className="!font-bold !text-[#111928]"
          />

          <p className="text-gray-500 text-sm max-w-[420px] mx-auto leading-relaxed">
            {t('home.delete-item-desc', 'Are you sure you want to delete this item?')}
          </p>

          <div className="flex flex-col-reverse sm:flex-row items-center justify-center sm:justify-end gap-3 mt-6 w-full">
            <Button
              variant="outline"
              onClick={() => setIsDeleteModalOpen(false)}
              className="w-full sm:w-auto rounded-xl px-8 py-2 font-semibold border border-gray-300 !bg-white !text-[#111928] hover:!bg-gray-50"
              disabled={isDeleting}
            >
              {t('home.cancel', 'Cancel')}
            </Button>
            <Button
              variant="dark"
              onClick={handleDelete}
              className="w-full sm:w-auto rounded-xl px-12 py-2 font-semibold !bg-[#D92D20] hover:!bg-[#B91C1C] !text-white !border-[#D92D20]"
              loading={isDeleting}
            >
              {t('home.yes-delete', 'Yes, Delete')}
            </Button>
          </div>
        </div>
      </CustomModal>
    </div>
  );
}
